import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Layout, theme } from 'antd';
import { Button } from '@/components/ui/Button';
import { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { BrandLockup, Logomark } from '@/components/Brand';
import { usePlatformTimezoneSync } from '@/lib/use-platform-timezone';
import { LAYOUT, NAV } from '@/theme/tokens';
import { AccountMenu } from './AccountMenu';
import { useAuth } from '@/auth/auth-context';
import { ALL_NAV_ITEMS, groupKeyForPath, NAVIGATION, NAVIGATION_FOOTER } from './navigation';
import type { NavItem } from './navigation';
import { PharmacyScopePicker } from './PharmacyScopePicker';
import { SidebarNav } from './SidebarNav';

const { Header, Sider, Content } = Layout;

export function AppLayout() {
  const { token } = theme.useToken();
  const { can } = useAuth();
  const location = useLocation();

  /*
   * The clock every date under this shell is rendered on. It sits here rather
   * than in a page because the whole tree has to re-render when it lands — see
   * `use-platform-timezone.ts`.
   */
  usePlatformTimezoneSync();

  const [collapsed, setCollapsed] = useState(false);

  // Longest matching prefix, so /catalog/medicines/:id keeps Medicines lit.
  const selectedKey = useMemo(() => {
    const matches = ALL_NAV_ITEMS.map((item) => item.path)
      .filter((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
      .sort((a, b) => b.length - a.length);
    return matches[0] ?? '/';
  }, [location.pathname]);

  /*
   * Sections open themselves when you are inside one, and otherwise stay shut.
   *
   * Held as state rather than derived, so opening a second section to compare
   * two things does not snap closed on the next render — but seeded from the
   * route, because landing on a deep link with every section folded leaves you
   * looking at a sidebar that does not contain the page you are on.
   */
  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const key = groupKeyForPath(selectedKey);
    return key ? [key] : [];
  });

  useEffect(() => {
    const key = groupKeyForPath(selectedKey);
    if (key) setOpenKeys((current) => (current.includes(key) ? current : [...current, key]));
  }, [selectedKey]);

  /*
   * Navigation is filtered by permission, not by role — same rule the backend
   * follows. Hiding a link is a courtesy, never a control: the route guard and
   * then the server both check again.
   */
  const visible = useMemo(
    () => (item: NavItem) => !item.permissions || item.permissions.some(can),
    [can],
  );

  return (
    <Layout className="h-full">
      <Sider
        /*
         * Always antd's "light" sider, in both themes. It used to be switched
         * with the mode, which mattered while the navigation was an antd `Menu`
         * that needed the matching dark palette. SidebarNav draws itself from
         * the CSS variables now, and leaving this to antd's default — which is
         * `dark` — would paint a near-black chrome over the ground set below.
         */
        theme="light"
        width={LAYOUT.siderWidth}
        collapsedWidth={LAYOUT.siderCollapsedWidth}
        collapsible
        collapsed={collapsed}
        trigger={null}
        breakpoint="lg"
        onBreakpoint={setCollapsed}
        style={{
          /*
           * White, with the content column a step back from it — the reverse
           * of what it was. The navigation is chrome and should sit still;
           * the page is the thing being worked on and reads better on a
           * ground that is not competing to be the brightest surface.
           */
          background: token.colorBgContainer,
          borderInlineEnd: `1px solid ${token.colorBorderSecondary}`,
        }}
        /*
         * The brand band and the account row stay put; only the navigation
         * between them scrolls. With the whole sider scrolling, a long menu
         * carried the logo off the top of the screen — and the band's hairline
         * is what lines up with the content header's, so it has to hold.
         *
         * Both halves of that are needed. antd puts `.ant-layout-sider-children`
         * between the Sider root and this markup, so a flex column on the root
         * alone leaves the wrapper a block: `flex-1` never resolves, the nav
         * grows past the viewport, and the root's `overflow: hidden` clips it
         * with no way to reach the last item.
         */
        classNames={{ body: 'flex min-h-0 flex-col' }}
        className="flex flex-col overflow-hidden!"
      >
        <div
          className="flex shrink-0 items-center"
          style={{
            height: LAYOUT.headerHeight,
            paddingInline: collapsed ? 0 : NAV.gutter,
            justifyContent: collapsed ? 'center' : undefined,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          {/* Collapsed, the wordmark would not fit; the cross alone still reads. */}
          {collapsed ? <Logomark size={30} /> : <BrandLockup height={34} />}
        </div>

        {/*
          * The pharmacy being worked on, at the top of the thing it scopes.
          *
          * It lived in the page header, three feet of screen away from the
          * Listings and Stock links it governs — so landing on an empty screen
          * and not knowing why was easy. Here it reads as what it is: the
          * context everything below it is filtered by.
          *
          * It narrows to its avatar when the sider does rather than unmounting;
          * a scope that is still filtering three screens has to stay visible.
          */}
        <div
          className="shrink-0 pt-3 pb-1"
          style={{ paddingInline: collapsed ? NAV.railInset : 12 }}
        >
          <PharmacyScopePicker collapsed={collapsed} />
        </div>

        <div className="app-scroll min-h-0 flex-1 overflow-y-auto">
          <SidebarNav
            label="Main"
            entries={NAVIGATION}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            collapsed={collapsed}
            visible={visible}
          />
        </div>

        <div className="shrink-0" style={{ borderTop: `1px solid ${token.colorBorderSecondary}` }}>
          {/*
            * One folding Settings row, sharing the open-section state with the
            * list above it. The two navs are separate because only one of them
            * scrolls, but a section is a section: keeping a second piece of
            * state for the footer would mean the route-seeding effect had to
            * know which list a path came from.
            */}
          <SidebarNav
            label="Settings"
            entries={NAVIGATION_FOOTER}
            openKeys={openKeys}
            onOpenChange={setOpenKeys}
            collapsed={collapsed}
            visible={visible}
          />

          <AccountMenu collapsed={collapsed} />
        </div>
      </Sider>

      <Layout>
        <Header
          className="sticky top-0 z-10 flex shrink-0 items-center gap-3"
          style={{
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <Button
            variant="ghost"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed((current) => !current)}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            aria-expanded={!collapsed}
          />
        </Header>

        <Content className="app-scroll overflow-y-auto">
          <div
            className="mx-auto px-5 py-6 sm:px-8 sm:py-8"
            style={{ maxWidth: LAYOUT.contentMaxWidth }}
          >
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>
  );
}
